import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { Button, ButtonIcon, H3 } from '../../components';
import { theme, toRem } from '../../design';

export const EnabledWrapper = styled.div`
  margin-top: ${toRem(20)};
  margin-bottom: ${toRem(20)};

  > p {
    color: ${theme.color.neutral[600]};
  }
`;

export const DisabledWrapper = styled.div`
  flex-direction: column;
  gap: ${toRem(6)};
  margin-top: ${toRem(20)};

  > p {
    &:last-child {
      color: ${theme.color.neutral[600]};
    }
  }
`;

export const LoadingSkeleton = styled(Skeleton)`
  && {
    width: 100%;
    border: ${toRem(1)} solid ${theme.color.neutral[200]};
    border-radius: ${toRem(8)};
    height: ${toRem(61)};
    margin-top: ${toRem(12)};
  }
`;

export const TipCalculationsContainer = styled.div<{ disabled?: boolean }>`
  flex-direction: column;
  align-items: flex-start;
  padding: ${toRem(24)};
  gap: ${toRem(16)};
  background: ${({ disabled }) => (!disabled ? theme.color.neutral[100] : theme.color.neutral[25])};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(8)};
  align-self: stretch;
  flex-grow: 0;
  margin-top: ${toRem(20)};
`;

export const Title = styled(H3)`
  font-size: ${toRem(14)};
`;

export const AddButton = styled(Button)<{ $tipShare?: boolean }>`
  width: fit-content !important;
  padding: 0 ${toRem(14)} !important;
  height: ${toRem(34)};
  font-size: ${toRem(11)};

  svg {
    height: ${toRem(20)};
    width: ${toRem(20)};
    fill: ${theme.color.neutral[700]};
  }

  ${({ $tipShare }) =>
    $tipShare &&
    css`
      height: ${toRem(32)};
      padding: ${toRem(8)} ${toRem(12)} !important;
      color: ${theme.color.neutral[500]};
      background-color: ${theme.color.neutral[50]};
      border: 1px solid ${theme.color.neutral[500]};

      svg {
        fill: ${theme.color.neutral[500]} !important;
      }

      &:hover,
      &:focus-visible {
        color: ${theme.color.neutral[700]};
        background-color: ${theme.color.neutral[50]};

        svg {
          fill: ${theme.color.neutral[700]} !important;
        }
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      svg {
        fill: ${theme.color.neutral[400]};
      }
    `}
`;

const SettingsBreakpoint = 1050;

export const SettingsLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  padding: ${toRem(24)};
  gap: ${toRem(24)};
  width: ${toRem(575)};
  margin: ${toRem(20)} 0;

  background: ${theme.color.neutral[100]};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: 8px;

  @media (max-width: ${SettingsBreakpoint}px) {
    width: 100%;
  }
`;

export const SettingsSection = styled.section`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  gap: ${toRem(14)};
  width: 100%;

  @media (min-width: ${SettingsBreakpoint}px) {
    div {
      max-width: ${toRem(265)};
    }
  }
`;

export const StyledIconButton = styled(ButtonIcon)<{ $keepStyles?: boolean; $noMargin?: boolean; $hideMobile?: boolean }>`
  width: ${toRem(24)} !important;
  height: ${toRem(24)} !important;
  margin-left: ${(props) => !props.$noMargin && toRem(8)};

  svg {
    fill: ${(props) => (props.$keepStyles ? `${theme.color.neutral[500]} !important` : theme.color.neutral[500])};
  }

  &:focus {
    border-color: ${theme.color.neutral[400]};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${theme.color.neutral[100]} !important;
    `}

  ${({ $hideMobile }) =>
    $hideMobile &&
    css`
      @media (max-width: ${theme.breakpoint.med}px) {
        display: none;
      }
    `}
`;

export const FieldsContainer = styled.div`
  flex-direction: column;
  gap: ${toRem(12)};
  width: 100%;
`;

export const StyledItem = styled.div`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: ${toRem(16)};
  gap: ${toRem(16)};
  background: ${theme.color.white};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(8)};
  height: ${toRem(61)};

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    gap: 0;
  }
`;

export const ItemContainer = styled.div<{ noGap?: boolean }>`
  flex-direction: column;
  width: 100%;
  padding: ${toRem(16)};
  background: ${theme.color.white};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(8)};

  ${({ noGap }) =>
    !noGap &&
    css`
      gap: ${toRem(8)};
    `}
`;

export const DetailsContainer = styled.ul`
  flex-direction: column;
  padding: ${toRem(16)};
  border-radius: ${toRem(theme.borderRadius.small)};
  background-color: ${theme.color.neutral[50]};

  p,
  span {
    line-height: 165%;
  }
`;

export const DetailsItem = styled.li`
  margin-left: ${toRem(16)};
  font-size: ${toRem(11)};
  color: ${theme.color.neutral[500]};
`;

export const StyledItemTextContainer = styled.div`
  flex-direction: column;
  overflow-wrap: anywhere;

  p {
    &:last-child {
      color: ${theme.color.neutral[500]};
    }
  }

  @media (max-width: ${theme.breakpoint.small}px) {
    p {
      width: ${toRem(290)};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    p {
      width: ${toRem(150)};
    }
  }
`;

export const ReadOnlyContainer = styled.div<{ $tipShare?: boolean }>`
  background-color: ${({ $tipShare }) => ($tipShare ? theme.color.neutral[50] : theme.color.neutral[100])};
  padding: ${toRem(12)};
  height: 100%;
  width: 100%;
  align-items: center;
  flex-grow: 1;
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(5.5)};
  justify-content: space-between;
`;

export const StyledLink = styled.a`
  color: ${theme.color.primary[600]};
`;
